import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { Application, LinkingService } from '@wind-log_library/fe-component-library';
import { environment } from 'environments/environment';
import { Observable, of, Subject } from 'rxjs';
import { catchError, distinctUntilChanged, map, switchMap, takeUntil } from 'rxjs/operators';
import { SiteTechnologyInfoQuery } from '../../services-api-multitech/graphql-multitech-api';
import { SiteTechnologyInfoService } from '../../services-api-multitech/site-technology-info.service';
import { NavigationCategory, NavigationService } from '../../services/data-services/navigation/navigation.service';

type TechnologyButton = {
  id: string;
  tooltip: string;
  url: string;
} & (
  | { icon: 'far fa-bolt'; iconType: 'font' }
  | { icon: 'powerplant-navigation-selected'; iconType?: undefined }
  | { icon: 'storage-navigation-selected'; iconType?: undefined }
);

interface TechnologyData {
  data: SiteTechnologyInfoQuery | null;
  module: string;
}

@Component({
  selector: 'app-site-technology-buttons',
  templateUrl: './site-technology-buttons.component.html',
  styleUrls: ['./site-technology-buttons.component.scss'],
  standalone: false,
})
export class SiteTechnologyButtonsComponent implements OnInit, OnDestroy {
  @Input() supportedModules = [
    'overview',
    'monitor',
    'troubleshoot',
    'analyze',
    'kpi-follow-up',
    'logbook',
    'budget',
    'check_revenues',
    'revenues',
    'tickets',
  ];

  public buttons: TechnologyButton[] = [];

  private readonly destroy$ = new Subject<void>();

  constructor(
    private navigationService: NavigationService,
    private siteTechnologyInfoService: SiteTechnologyInfoService,
  ) {}

  public ngOnInit(): void {
    this.navigationService
      .getNavigationObservable([
        NavigationCategory.Module,
        NavigationCategory.Level,
        NavigationCategory.ParkLevelSelectedTurbines,
      ])
      .pipe(
        map(navigation => ({
          module: navigation.module,
          inverterIds:
            navigation.level === 'park' && this.supportedModules.includes(navigation.module)
              ? navigation.selectedTurbineIds ?? []
              : [],
        })),
        distinctUntilChanged(
          (previous, current) =>
            previous.module === current.module &&
            previous.inverterIds.length === current.inverterIds.length &&
            previous.inverterIds.every((id, index) => id === current.inverterIds[index]),
        ),
        switchMap(({ module, inverterIds }): Observable<TechnologyData> =>
          inverterIds.length
            ? this.siteTechnologyInfoService.getData(inverterIds).pipe(
                map(data => ({ data, module })),
                catchError(() => of({ data: null, module })),
              )
            : of({ data: null, module }),
        ),
        takeUntil(this.destroy$),
      )
      .subscribe(({ data, module }) => this.setButtons(data, module));
  }

  public ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private setButtons(data: SiteTechnologyInfoQuery | null, module: string): void {
    const assets = data?.ASSETS ?? [];
    const multitech = assets.find(
      asset => !!asset?._id && (!!asset.SOLAR?._id || !!asset.WIND?._id || !!asset.STORAGE?._id),
    );
    const solar = assets.find(asset => !!asset.SOLAR?._id)?.SOLAR;
    const storage = assets.find(asset => !!asset.STORAGE?._id)?.STORAGE;

    this.buttons = [
      multitech && {
        id: `multitech-${multitech._id}`,
        icon: 'far fa-bolt' as const,
        iconType: 'font' as const,
        tooltip: 'SITE_TECHNOLOGY_BUTTONS.GO_TO_MULTITECH',
        url: `${LinkingService.getBaseUrlByEnv(Application.ENERGY, environment)}/${module}/site/${multitech._id}`,
      },
      solar?._id && {
        id: `solar-${solar._id}`,
        icon: 'powerplant-navigation-selected' as const,
        tooltip: 'SITE_TECHNOLOGY_BUTTONS.GO_TO_SOLAR',
        url: `${LinkingService.getBaseUrlByEnv(Application.SOLAR, environment)}/${module}/powerplant/${encodeURIComponent(solar._id)}`,
      },
      storage?._id && {
        id: `storage-${storage._id}`,
        icon: 'storage-navigation-selected' as const,
        tooltip: 'SITE_TECHNOLOGY_BUTTONS.GO_TO_STORAGE',
        url: `${LinkingService.getBaseUrlByEnv(Application.STORAGE, environment)}/${module}/storage/${storage._id}`,
      },
    ].filter((button): button is TechnologyButton => !!button);
  }
}
